import { formatMs } from '@/app/pages/Diagnostics/diagnosticsUtils'
import type { NetworkVerdict } from '@/components/network-status/networkVerdict'

const Row = ({ label, value, note, testId }: { label: string; value: string; note?: string; testId?: string }) => (
	<div className='flex items-baseline justify-between gap-3'>
		<span className='text-control text-muted-foreground'>{label}</span>
		<span className='flex items-baseline gap-1.5'>
			<span className='text-control font-medium tabular-nums' data-testid={testId}>
				{value}
			</span>
			{note ? <span className='text-badge text-muted-foreground tabular-nums'>{note}</span> : null}
		</span>
	</div>
)

const minutesAgo = (ms: number | undefined): string | undefined => {
	if (ms === undefined) {
		return undefined
	}
	const minutes = Math.round(ms / 60_000)
	return minutes < 1 ? 'just now' : `${minutes} min ago`
}

export const NetworkStatusPopoverBody = ({ verdict }: { verdict: NetworkVerdict }) => {
	const { quality, dnsMs, connectMs, connectAgeMs, waitingMs, waitingP95Ms, downloadMs, requestCount } = verdict

	if (quality === 'offline') {
		return <Row label='Connection' value='offline' testId='network-status-quality' />
	}

	return (
		<div className='flex flex-col gap-1'>
			{dnsMs === undefined ? null : <Row label='DNS' value={formatMs(dnsMs)} testId='network-status-dns' />}
			{connectMs === undefined ? null : (
				<Row
					label='Connect'
					value={formatMs(connectMs)}
					note={minutesAgo(connectAgeMs)}
					testId='network-status-connect'
				/>
			)}
			{waitingMs === undefined ? (
				<Row label='Waiting' value='too few requests' testId='network-status-waiting-none' />
			) : (
				<Row
					label='Waiting'
					value={formatMs(waitingMs)}
					note={waitingP95Ms === undefined ? undefined : `p95 ${formatMs(waitingP95Ms)}`}
					testId='network-status-waiting'
				/>
			)}
			{downloadMs === undefined ? null : (
				<Row label='Download' value={formatMs(downloadMs)} testId='network-status-download' />
			)}

			<p className='text-badge text-muted-foreground pt-0.5' data-testid='network-status-window-note'>
				{requestCount ? `Last 5 min · ${requestCount} requests` : 'Last 5 min'}
			</p>
			<p className='text-badge text-muted-foreground' data-testid='network-status-legend'>
				Connect is your network. Waiting much larger than connect means the server, not your connection.
			</p>
		</div>
	)
}
